import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Ruler, Loader2, FileText, Copy } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

/**
 * MaterialEstimateDialog
 * ----------------------
 * Chat-style AI helper that estimates materials, quantities and reference
 * prices for a given service. Admin can copy the suggested description or
 * use the full technical breakdown as the item observation.
 */
export default function MaterialEstimateDialog({ open, onOpenChange, onUseDescription, onUseObservation }) {
  const [service, setService] = useState("");
  const [width, setWidth] = useState("");
  const [length, setLength] = useState("");
  const [extra, setExtra] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const reset = () => { setService(""); setWidth(""); setLength(""); setExtra(""); setResult(""); };

  const run = async () => {
    if (!service.trim()) { toast.error("Descreva o serviço"); return; }
    setLoading(true);
    setResult("");
    try {
      const r = await api.post("/ai/material-estimate", {
        service: service.trim(),
        width_m: width ? parseFloat(String(width).replace(",", ".")) : null,
        length_m: length ? parseFloat(String(length).replace(",", ".")) : null,
        extra: extra.trim() || null,
      });
      setResult((r.data?.text || "").trim());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao consultar a IA");
    } finally {
      setLoading(false);
    }
  };

  const extractDescription = () => {
    // The first non-empty line is the "DESCRIÇÃO SUGERIDA"
    const lines = (result || "").split(/\n+/).map((l) => l.trim()).filter(Boolean);
    return lines[0] || "";
  };

  const copyAll = async () => {
    try { await navigator.clipboard.writeText(result); toast.success("Estimativa copiada"); }
    catch { toast.error("Falha ao copiar"); }
  };

  const useAsDesc = () => {
    const d = extractDescription();
    if (!d) { toast.error("Nada a usar"); return; }
    onUseDescription?.(d);
    onOpenChange(false);
    reset();
  };

  const useAsObs = () => {
    if (!result.trim()) { toast.error("Nada a usar"); return; }
    onUseObservation?.(result.trim());
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1e3a8a]">
            <Sparkles className="w-5 h-5 text-[#7c3aed]" /> Materiais & valores com IA
          </DialogTitle>
          <DialogDescription>
            Descreva o serviço, informe medidas e a IA sugere materiais, quantidades e valores aproximados.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1 block">Serviço *</label>
            <Textarea
              rows={2}
              value={service}
              onChange={(e) => setService(e.target.value)}
              placeholder="Ex: colocação de forro de drywall no refeitório"
              data-testid="estimate-service"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-bold text-slate-600 mb-1 block">Largura (m)</label>
              <Input value={width} onChange={(e) => setWidth(e.target.value)} inputMode="decimal" placeholder="5" data-testid="estimate-width" />
            </div>
            <div>
              <label className="text-[11px] font-bold text-slate-600 mb-1 block">Comprimento (m)</label>
              <Input value={length} onChange={(e) => setLength(e.target.value)} inputMode="decimal" placeholder="5" data-testid="estimate-length" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-bold text-slate-600 mb-1 block">Observações adicionais (opcional)</label>
            <Textarea
              rows={2}
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder="Ex: pé-direito 3m, instalação de luminárias"
              data-testid="estimate-extra"
            />
          </div>
          <Button
            onClick={run}
            disabled={loading || !service.trim()}
            className="w-full bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
            data-testid="estimate-run"
          >
            {loading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Calculando...</> : <><Ruler className="w-4 h-4 mr-1.5" /> Estimar materiais e valores</>}
          </Button>

          {result && (
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-slate-700">Resultado</p>
                <Button size="sm" variant="outline" onClick={copyAll} className="h-7 text-[11px]" data-testid="estimate-copy">
                  <Copy className="w-3 h-3 mr-1" /> Copiar tudo
                </Button>
              </div>
              <Textarea
                value={result}
                onChange={(e) => setResult(e.target.value)}
                rows={12}
                className="font-mono text-xs"
                data-testid="estimate-result"
              />
              <div className="flex gap-2 flex-wrap">
                {onUseDescription && (
                  <Button size="sm" onClick={useAsDesc} className="h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="estimate-use-desc">
                    <FileText className="w-3 h-3 mr-1" /> Usar como descrição
                  </Button>
                )}
                {onUseObservation && (
                  <Button size="sm" variant="outline" onClick={useAsObs} className="h-8 text-[11px]" data-testid="estimate-use-obs">
                    Usar como observação
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-slate-400">
                Valores são aproximações da IA. Sempre revise antes de incluir no orçamento final.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); reset(); }}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
